import React, { createContext, useContext, useEffect, useState } from "react";
import { IAuditNote, IUnsubmittedAuditNote, TEditableAuditNote } from "../interfaces/IAuditNote";


interface IAuditNoteContext {
    notes: { [key: string]: TEditableAuditNote; };
    addNote: (note: TEditableAuditNote) => void;
    removeNote: (note: TEditableAuditNote["id"]) => void;
    setNotes: (notes: { [key: string]: TEditableAuditNote; }) => void;
    setNote: (note: TEditableAuditNote) => void;
};

const noop = () => undefined;

const defaultValue: IAuditNoteContext = {
    notes: {},
    addNote: noop,
    removeNote: noop,
    setNotes: noop,
    setNote: noop,
};

const context = createContext<IAuditNoteContext>(defaultValue);
const { Provider } = context;

export const useAuditNoteContext = () => {
    const noteContext = useContext(context);
    if (!noteContext) {
        throw Error("Cannot call useAuditNoteContext hook outside AuditNoteProvider")
    }

    return noteContext
};

interface IAuditNoteProviderProps {
    initial?: IAuditNoteContext["notes"];
    data?: IAuditNoteContext["notes"];
    onNotesChanged?: (notes: IAuditNoteContext["notes"]) => void;
};

// Provider
export const AuditNoteProvider: React.FC<IAuditNoteProviderProps> = ({
    children,
    initial = {},
    data,
    onNotesChanged = noop
}) => {
    const [notes, setNotes] = useState(data || initial);

    useEffect(() => {
        if (typeof data !== "undefined") {
            setNotes(data)
        };
    }, [data]);

    useEffect(() => {
        onNotesChanged(notes);
    }, [notes]); //eslint-disable-line

    const addNote = (note: TEditableAuditNote) => {
        setNotes((prevNotes) => ({ ...prevNotes, [note.id]: note }))
    };

    const setNote = (note: TEditableAuditNote) => {
        setNotes((prevNotes) => ({ ...prevNotes, [note.id]: note }))
    };

    const removeNote = (noteId: TEditableAuditNote["id"]) => {
        setNotes((prevNotes) => {
            let nextNotes = { ...prevNotes };
            delete nextNotes[noteId];
            return nextNotes;
        });
    };


    const contextData: IAuditNoteContext = {
        notes,
        addNote,
        removeNote,
        setNote,
        setNotes
    };

    return (
        <Provider value={contextData}>
            {children}
        </Provider>
    );
};
